import { GET, POST, PUT, DELETE } from '../dataSaver';
import { withQueryParams } from './portalTilesApi';

export const PARTNERS_URL = 'user-management-service/portal/partners';
export const PARTNER_LOGO_KEY = 'logo';

/** Normalize API list / single responses into an array of partners. */
export const normalizePartnersList = (data) => {
  if (Array.isArray(data)) return data.filter(Boolean);
  if (Array.isArray(data?.partners)) return data.partners.filter(Boolean);
  if (Array.isArray(data?.content)) return data.content.filter(Boolean);
  if (Array.isArray(data?.data)) return data.data.filter(Boolean);
  if (data && typeof data === 'object' && data.partnerId) return [data];
  return [];
};

/**
 * Normalize partner from API / form.
 * Supports legacy string values and the new partner object shape.
 */
export const normalizePartner = (partner) => {
  if (partner == null || partner === '') return null;
  if (typeof partner === 'string') {
    const partnerName = partner.trim();
    if (!partnerName) return null;
    return {
      partnerId: '',
      partnerName,
      description: '',
      logoUrl: '',
      active: true,
    };
  }
  if (typeof partner !== 'object') return null;
  const partnerName = String(partner.partnerName || '').trim();
  const partnerId = String(partner.partnerId || partner.id || '').trim();
  if (!partnerName && !partnerId) return null;
  return {
    partnerId,
    partnerName,
    description: partner.description || '',
    logoUrl: String(
      partner.logoUrl ||
        partner.logo ||
        partner.logoURL ||
        partner.organizationLogo ||
        partner.fileURL ||
        partner.logo?.fileURL ||
        ''
    ).trim(),
    active: partner.active !== false,
    createdDate: partner.createdDate,
    lastModifiedDate: partner.lastModifiedDate,
    createdBy: partner.createdBy,
    lastModifiedBy: partner.lastModifiedBy,
  };
};

/** Display label for filters / cards (works with string or object). */
export const getPartnerLabel = (partner) => {
  const normalized = normalizePartner(partner);
  return normalized?.partnerName || '';
};

/** Payload value for create/update APIs — object or null when cleared. */
export const toPartnerPayload = (partner) => {
  const normalized = normalizePartner(partner);
  if (!normalized) return null;
  // Prefer sending a real partner record; legacy free-text has no id.
  if (!normalized.partnerId) {
    return {
      partnerName: normalized.partnerName,
      description: normalized.description || '',
      logoUrl: normalized.logoUrl || '',
      active: normalized.active !== false,
    };
  }
  return {
    partnerId: normalized.partnerId,
    partnerName: normalized.partnerName,
    description: normalized.description || '',
    logoUrl: normalized.logoUrl || '',
    active: normalized.active !== false,
  };
};

/**
 * Demo Studio share create/update partner shape.
 * Backend validates by partnerId and returns the canonical partnerName.
 */
export const toDemoSharePartnerPayload = (partner) => {
  const normalized = normalizePartner(partner);
  if (!normalized?.partnerId) return null;
  return {
    partnerId: normalized.partnerId,
    partnerName: normalized.partnerName || '',
  };
};

/** Prefer backend error text over generic axios / fallback messages. */
export const getApiErrorMessage = (errorOrResponse, fallback = 'Something went wrong') => {
  const data =
    errorOrResponse?.response?.data ??
    errorOrResponse?.data ??
    null;

  if (typeof data === 'string' && data.trim()) return data.trim();

  if (data && typeof data === 'object') {
    const candidates = [
      data.message,
      data.errorMessage,
      data.error,
      data.detail,
      data.title,
      Array.isArray(data.errors) ? data.errors[0] : null,
      typeof data.errors === 'string' ? data.errors : null,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
      if (
        candidate &&
        typeof candidate === 'object' &&
        typeof candidate.message === 'string' &&
        candidate.message.trim()
      ) {
        return candidate.message.trim();
      }
    }
  }

  const axiosMessage = String(errorOrResponse?.message || '').trim();
  if (
    axiosMessage &&
    !/^Request failed with status code \d+$/i.test(axiosMessage) &&
    axiosMessage !== fallback
  ) {
    return axiosMessage;
  }

  return fallback;
};

const throwIfRequestFailed = (response, fallbackMessage) => {
  const status = response?.response?.status ?? response?.status;
  if (response?.isAxiosError || (typeof status === 'number' && status >= 400)) {
    const err = new Error(getApiErrorMessage(response, fallbackMessage));
    err.response = response?.response || response;
    throw err;
  }
};

export const fetchPartners = async ({ search, activeOnly } = {}) => {
  const { data } = await GET(
    withQueryParams(PARTNERS_URL, {
      search,
      activeOnly:
        activeOnly === undefined || activeOnly === null
          ? undefined
          : activeOnly
            ? 'true'
            : 'false',
    })
  );
  return normalizePartnersList(data).map(normalizePartner).filter(Boolean);
};

export const fetchPartnerById = async (partnerId) => {
  const id = String(partnerId || '').trim();
  if (!id) return null;
  const response = await GET(`${PARTNERS_URL}/${encodeURIComponent(id)}`);
  throwIfRequestFailed(response, 'Failed to load partner');
  return normalizePartner(response?.data ?? response);
};

/**
 * Upload Partner logo to S3 and update partner.logoUrl.
 * POST /portal/partners/{partnerId}/logo  (multipart field: logo)
 */
export const uploadPartnerLogo = async (partnerId, logoFile) => {
  const id = String(partnerId || '').trim();
  if (!id) throw new Error('Partner id is required');
  if (!(logoFile instanceof File)) throw new Error('Logo file is required');
  const formData = new FormData();
  formData.append(PARTNER_LOGO_KEY, logoFile, logoFile.name);
  const response = await POST(
    `${PARTNERS_URL}/${encodeURIComponent(id)}/logo`,
    formData
  );
  throwIfRequestFailed(response, 'Failed to upload partner logo');
  return normalizePartner(response?.data ?? response);
};

/**
 * Create partner via multipart/form-data (always).
 * Appends logo only when a File is provided so create works with or without a logo.
 */
export const createPartner = async (payload, logoFile = null) => {
  const partnerName = String(payload?.partnerName || '').trim();
  const description = String(payload?.description || '').trim();
  const active = payload?.active !== false;
  const file =
    logoFile instanceof File
      ? logoFile
      : payload?.logoFile instanceof File
        ? payload.logoFile
        : null;

  const formData = new FormData();
  formData.append('partnerName', partnerName);
  formData.append('description', description);
  formData.append('active', String(active));
  if (file) {
    formData.append(PARTNER_LOGO_KEY, file, file.name);
  } else {
    const logoUrl = String(payload?.logoUrl || '').trim();
    if (logoUrl) formData.append('logoUrl', logoUrl);
  }

  const response = await POST(PARTNERS_URL, formData);
  throwIfRequestFailed(response, 'Failed to create partner');
  return normalizePartner(response?.data ?? response);
};

export const updatePartner = async (partnerId, payload) => {
  const id = String(partnerId || '').trim();
  if (!id) throw new Error('Partner id is required');
  const body = {
    partnerName: String(payload?.partnerName || '').trim(),
    description: String(payload?.description || '').trim(),
    active: payload?.active !== false,
  };
  if (Object.prototype.hasOwnProperty.call(payload || {}, 'logoUrl')) {
    body.logoUrl = String(payload.logoUrl || '').trim();
  }
  const response = await PUT(
    `${PARTNERS_URL}/${encodeURIComponent(id)}`,
    body
  );
  throwIfRequestFailed(response, 'Failed to update partner');
  return normalizePartner(response?.data ?? response);
};

/**
 * Update partner details, then optionally upload a new logo file.
 */
export const updatePartnerWithLogo = async (
  partnerId,
  payload,
  logoFile = null
) => {
  const updated = await updatePartner(partnerId, payload);
  if (!(logoFile instanceof File)) return updated;
  return uploadPartnerLogo(partnerId, logoFile);
};

export const deletePartner = async (partnerId) => {
  const id = String(partnerId || '').trim();
  if (!id) throw new Error('Partner id is required');
  const response = await DELETE(`${PARTNERS_URL}/${encodeURIComponent(id)}`);
  throwIfRequestFailed(response, 'Failed to delete partner');
  return true;
};
